import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';

import { DevFlag, injectDevFlag } from '@recruitee/dev-flags-data';
import { TranslatePipe } from '@recruitee/i18n';
import { ThemeName } from '@recruitee/theme';
import { ButtonComponent, HeadingComponent, IconComponent } from '@recruitee/user-interface';

import { SvgAssetComponent } from '../../../common';

@Component({
  selector: 'rt-kiwi-hr-promo',
  template: `
    <rti-svg-asset [name]="logotype()" centerHorizontally="false" margin-bottom></rti-svg-asset>
    <div
      [attr.layout]="hasRowLayout() || null"
      [attr.layout-align-items-center]="hasRowLayout() || null"
    >
      <div layout-vertical margin-right>
        <rt-heading
          [variant]="headingVariant()"
          [attr.margin-bottom-small-x]="hasHeadingMarginBottom() || null"
        >
          {{ 'rti.kiwi_hr.promo.heading' | translate }}
        </rt-heading>
        <span [attr.text-semi-medium]="subheadingFontWeight() === 'medium' || null">
          {{ 'rti.kiwi_hr.promo.subheading' | translate }}
        </span>
      </div>
      <rt-button
        variant="primary"
        (click)="ctaClick.emit()"
        class="kiwi-colors"
        [attr.margin-top-large]="(hasButtonLargeMarginTop() && !hasRowLayout()) || null"
        [attr.margin-top-medium]="(!hasButtonLargeMarginTop() && !hasRowLayout()) || null"
      >
        {{ 'rti.kiwi_hr.promo.cta' | translate }}
        <rt-icon name="open-in-new" colorToken="colorIconOnprimaryDefault"></rt-icon>
      </rt-button>
    </div>
  `,
  styleUrl: 'kiwi-hr-promo.component.less',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, IconComponent, HeadingComponent, TranslatePipe, SvgAssetComponent],
  host: {
    '[class.button-fit-content]': 'buttonFitContent()',
    '[class.has-padding]': 'hasPadding()',
  },
})
export class KiwiHrPromoComponent {
  protected readonly hasNewCandidateProfile = injectDevFlag(DevFlag.CandidateProfileRefresh);

  public hasBigHeading = input.required<boolean, boolean | string>({ transform: booleanAttribute });

  public buttonFitContent = input.required<boolean, boolean | string>({
    transform: booleanAttribute,
  });

  public hasButtonLargeMarginTop = input.required<boolean, boolean | string>({
    transform: booleanAttribute,
  });

  public hasHeadingMarginBottom = input.required<boolean, boolean | string>({
    transform: booleanAttribute,
  });

  public subheadingFontWeight = input.required<'default' | 'medium'>();

  public hasPadding = input.required<boolean, boolean | string>({ transform: booleanAttribute });

  public theme = input.required<ThemeName>();

  /**
   * This is for backwards compatibility with the old profile design. Set true if component is
   * used on candidate profile.
   * @protected
   */
  public hasRowLayout = input<boolean, boolean | string>(false, {
    transform: (value: string | boolean) => !this.hasNewCandidateProfile && booleanAttribute(value),
    alias: 'allowRowLayout',
  });

  public ctaClick = output<void>();

  protected headingVariant = computed(() =>
    this.hasBigHeading() ? 'heading large-x-dialog-header' : 'heading',
  );

  protected logotype = computed(() =>
    this.theme() === 'dark' ? 'recruiteeKiwiDark' : 'recruiteeKiwiLight',
  );
}
